import { modelData } from 'app/lib/modelData'
// Thin typed helpers over the non-hook GraphQL client for the multi-step Workflow engine. Used from
// the builder's Save/Activate handlers (outside React hooks), per the urql skill.
import type { CreateWorkflowInput, UpdateWorkflowInput, Workflow, WorkflowListResult } from 'app/generated/workflows'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import {
  CREATE_WORKFLOW_MUTATION,
  DELETE_WORKFLOW_MUTATION,
  GET_WORKFLOW_QUERY,
  LIST_WORKFLOWS_QUERY,
  SET_WORKFLOW_CATEGORY_MUTATION,
  UPDATE_WORKFLOW_MUTATION,
} from './graphql'

const workflowJsonFields = ['builderJson', 'edgesJson', 'nodesJson', 'settingsJson', 'triggerJson'] as const

export type RemoteResult<T> = { data?: T; error?: string }

export async function listRemoteWorkflows(params?: {
  search?: string
  pagination?: { page?: number; limit?: number }
  sort?: { field: string; direction: string }
}): Promise<RemoteResult<WorkflowListResult>> {
  const { data, error } = await gqlQuery<{ workflows: WorkflowListResult }>(LIST_WORKFLOWS_QUERY, params ?? {})
  return { data: data?.workflows, error: error?.message }
}

export async function getRemoteWorkflow(id: string): Promise<RemoteResult<Workflow>> {
  const { data, error } = await gqlQuery<{ workflow: Workflow }>(GET_WORKFLOW_QUERY, { id })
  return { data: data?.workflow ?? undefined, error: error?.message }
}

export async function createRemoteWorkflow(input: CreateWorkflowInput): Promise<RemoteResult<Workflow>> {
  const { data, error } = await gqlMutation(CREATE_WORKFLOW_MUTATION, { input })
  return { data: modelData(data?.createWorkflow, workflowJsonFields), error: error?.message }
}

export async function updateRemoteWorkflow(id: string, input: UpdateWorkflowInput): Promise<RemoteResult<Workflow>> {
  const { data, error } = await gqlMutation(UPDATE_WORKFLOW_MUTATION, { id, input })
  return { data: modelData(data?.updateWorkflow, workflowJsonFields), error: error?.message }
}

export async function deleteRemoteWorkflow(id: string): Promise<RemoteResult<boolean>> {
  const { data, error } = await gqlMutation(DELETE_WORKFLOW_MUTATION, { id })
  return { data: data?.deleteWorkflow, error: error?.message }
}

/** Marks a workflow transactional/marketing at the engine. OPS schema, super-only (api #581):
 *  category is an admin claim — the engine treats every workflow as marketing until a super says
 *  otherwise — so this is called from save only when the current user is a super. */
export async function setRemoteWorkflowCategory(
  id: string,
  category: 'marketing' | 'transactional',
): Promise<RemoteResult<Pick<Workflow, 'id' | 'category'>>> {
  const result = await opsClient
    .mutation(SET_WORKFLOW_CATEGORY_MUTATION, {
      id,
      category: category === 'marketing' ? 'MARKETING' : 'TRANSACTIONAL',
    })
    .toPromise()
  return { data: modelData(result.data?.setWorkflowCategory), error: result.error?.message }
}
